(function () {
  function clearPendingState() {
    if (document && document.documentElement) {
      document.documentElement.classList.remove("b2b-images-pending");
    }
  }

  var root = window.B2BImagePersonalization || {};
  if (root.enabled === false) {
    clearPendingState();
    return;
  }

  function sanitizeCompanyCode(value) {
    if (!value) return "";
    return String(value).trim().replace(/[^A-Za-z0-9_-]/g, "");
  }

  var companyCode = sanitizeCompanyCode(root.companyCode);
  if (!companyCode) {
    clearPendingState();
    return;
  }

  var processed = new WeakSet();
  var availabilityCache = new Map();
  var PERSONALIZE_SELECTOR = '[data-b2b-personalize="true"] img';
  // 21 Oct 2026 (Ryan, Lindum): the full-screen viewer (snippets/gallery-viewer.liquid, GalleryViewer in main.js)
  // builds its own images from the product's original media URLs, outside the data-b2b-personalize wrappers, so a
  // click to view a photo full screen showed the generic "your logo here" photo. Its zoomed image and its thumbnails
  // get the same company swap as the gallery.
  var VIEWER_SELECTOR = ".gallery-viewer__zoom-image, .gallery-viewer__thumb-img";
  var ALL_SELECTOR = PERSONALIZE_SELECTOR + ", " + VIEWER_SELECTOR;

  function splitSrcsetEntry(entry) {
    var trimmed = entry.trim();
    if (!trimmed) return null;

    var descriptorMatch = trimmed.match(/\s+([0-9]+[wx])$/);
    if (!descriptorMatch) return { url: trimmed, descriptor: "" };

    var descriptor = descriptorMatch[1];
    var url = trimmed.slice(0, trimmed.length - descriptorMatch[0].length);
    return { url: url, descriptor: descriptor };
  }

  function buildPersonalizedUrl(rawUrl, code, baseOverride) {
    if (!rawUrl) return "";

    var parsed;
    try {
      parsed = new URL(rawUrl, window.location.origin);
    } catch (err) {
      return "";
    }

    var pathname = parsed.pathname || "";
    var slashIndex = pathname.lastIndexOf("/");
    var dir = slashIndex >= 0 ? pathname.slice(0, slashIndex + 1) : "";
    var file = slashIndex >= 0 ? pathname.slice(slashIndex + 1) : pathname;

    var match = file.match(/^(.*?)(_(?:\d+x\d*|x\d+))?(\.[A-Za-z0-9]+)$/);
    if (!match) return "";

    var baseName = baseOverride || match[1];
    var sizeSuffix = match[2] || "";
    var extension = match[3];

    if (baseName.endsWith("_" + code)) {
      return parsed.toString();
    }

    parsed.pathname = dir + baseName + "_" + code + sizeSuffix + extension;
    return parsed.toString();
  }

  function stripTrailingUuid(baseName) {
    if (!baseName) return "";
    return String(baseName).replace(/_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, "");
  }

  function isRandomTailToken(token) {
    if (!token) return false;
    return (
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(token) ||
      /^[0-9a-f]{10,}$/i.test(token) ||
      /^[0-9]{8,}$/i.test(token)
    );
  }

  function stripShotNoiseByConvention(baseName) {
    if (!baseName) return "";
    var parts = String(baseName).split("_");

    // Only apply this fallback for B2B naming scheme:
    // B2B_SKU_Product_Shot (product part may include extra underscores).
    if (parts.length < 4 || parts[0] !== "B2B") return baseName;

    var last = parts[parts.length - 1];
    if (!isRandomTailToken(last)) return baseName;

    return parts.slice(0, -1).join("_");
  }

  function buildPersonalizedUrlCandidates(rawUrl, code) {
    if (!rawUrl) return [];

    var parsed;
    try {
      parsed = new URL(rawUrl, window.location.origin);
    } catch (err) {
      return [];
    }

    var pathname = parsed.pathname || "";
    var slashIndex = pathname.lastIndexOf("/");
    var file = slashIndex >= 0 ? pathname.slice(slashIndex + 1) : pathname;
    var match = file.match(/^(.*?)(_(?:\d+x\d*|x\d+))?(\.[A-Za-z0-9]+)$/);
    if (!match) {
      var single = buildPersonalizedUrl(rawUrl, code);
      return single ? [{ url: single, base: "" }] : [];
    }

    var baseName = match[1];
    var baseCandidates = [baseName];
    var uuidStripped = stripTrailingUuid(baseName);
    if (uuidStripped && uuidStripped !== baseName) baseCandidates.push(uuidStripped);
    var conventionStripped = stripShotNoiseByConvention(baseName);
    if (conventionStripped && baseCandidates.indexOf(conventionStripped) === -1) {
      baseCandidates.push(conventionStripped);
    }

    var urls = [];
    baseCandidates.forEach(function (candidateBase) {
      var built = buildPersonalizedUrl(rawUrl, code, candidateBase);
      if (!built) return;
      if (urls.some(function (entry) { return entry.url === built; })) return;
      urls.push({ url: built, base: candidateBase });
    });

    return urls;
  }

  function rewriteSrcset(srcset, code, baseOverride) {
    if (!srcset) return "";
    return srcset
      .split(",")
      .map(function (entry) {
        var parsed = splitSrcsetEntry(entry);
        if (!parsed || !parsed.url) return entry;

        var altUrl = buildPersonalizedUrl(parsed.url, code, baseOverride);
        if (!altUrl) return entry.trim();

        return parsed.descriptor ? altUrl + " " + parsed.descriptor : altUrl;
      })
      .join(", ");
  }

  // 13 Oct 2026 (trade collection pages, AIM sign-up): every check used to download the full-size file with a
  // fresh cache-buster, for every product photo on the page at once, and again for every photo after each sort or
  // filter. A company with no branded photos got one 404 per photo per candidate name (the trade home page lists
  // up to 250 products: hundreds of 404s on each visit). Now each file is checked once per visit (kept in
  // sessionStorage for 30 minutes, so the next page and a re-sorted grid do not ask again), checks of the same
  // file share one request, and the check asks for a 32px copy, so a hit costs a few hundred bytes.
  // The 10-minute b2bv token keeps a newly made photo set from being hidden for long behind a cached 404.
  var SESSION_KEY = "b2b-img-sets:" + companyCode;
  var SESSION_TTL = 30 * 60 * 1000;
  var sessionCache = loadSessionCache();
  var saveTimer = null;

  function loadSessionCache() {
    try {
      var raw = JSON.parse(window.sessionStorage.getItem(SESSION_KEY) || "null");
      if (raw && raw.t && raw.r && Date.now() - raw.t < SESSION_TTL) return raw;
    } catch (err) {
      /* storage blocked or full: checks just are not remembered */
    }
    return { t: Date.now(), r: {} };
  }

  function saveSessionCache() {
    if (saveTimer) return;
    saveTimer = setTimeout(function () {
      saveTimer = null;
      try {
        if (Object.keys(sessionCache.r).length > 2000) sessionCache = { t: Date.now(), r: {} };
        window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(sessionCache));
      } catch (err) {
        /* storage blocked or full */
      }
    }, 400);
  }

  function cacheKey(url) {
    try {
      return new URL(url, window.location.origin).pathname;
    } catch (err) {
      return String(url).split("?")[0];
    }
  }

  function checkImageExists(url) {
    if (!url) return Promise.resolve(false);
    var key = cacheKey(url);
    if (availabilityCache.has(key)) return availabilityCache.get(key);

    var pending;
    if (Object.prototype.hasOwnProperty.call(sessionCache.r, key)) {
      pending = Promise.resolve(sessionCache.r[key] === 1);
    } else {
      pending = new Promise(function (resolve) {
        var probe = new Image();
        function done(exists) {
          sessionCache.r[key] = exists ? 1 : 0;
          saveSessionCache();
          resolve(exists);
        }
        probe.onload = function () { done(true); };
        probe.onerror = function () { done(false); };
        probe.src = url.split("?")[0] + "?width=32&b2bv=" + Math.floor(Date.now() / 600000);
      });
    }
    availabilityCache.set(key, pending);
    return pending;
  }

  // 13 Oct 2026: photos in product grids (collection pages, the trade home page, carousels of product cards) are
  // only checked when their card comes within about a screen of the viewport, so a long grid no longer fires every
  // check on load. The whole card is done together, hover photos included. Everything else (the product page
  // gallery) is checked straight away, as before.
  var GRID_CARD = "product-block, [data-product-card]";
  var nearObserver = null;
  if (typeof window.IntersectionObserver === "function") {
    try {
      nearObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          nearObserver.unobserve(entry.target);
          entry.target.querySelectorAll(PERSONALIZE_SELECTOR).forEach(function (img) {
            try {
              swapImage(img);
            } catch (err) {
              if (window.console && console.warn) {
                console.warn("b2b image personalization skipped an image", err);
              }
            }
          });
        });
      }, { rootMargin: "800px 0px" });
    } catch (err) {
      nearObserver = null;
    }
  }

  function queueImage(img) {
    if (!img || processed.has(img)) return;
    var card = nearObserver && img.closest ? img.closest(GRID_CARD) : null;
    if (card) {
      nearObserver.observe(card);
      return;
    }
    swapImage(img);
  }

  function swapImage(img) {
    if (!img || processed.has(img)) return;
    processed.add(img);

    var originalSrc = img.getAttribute("src") || img.currentSrc || "";
    if (!originalSrc) return;

    var candidates = buildPersonalizedUrlCandidates(originalSrc, companyCode);
    if (!candidates.length) return;

    var originalSrcset = img.getAttribute("srcset") || "";
    var found = null;
    var sequence = Promise.resolve(false);
    candidates.forEach(function (candidate) {
      sequence = sequence.then(function (alreadyFound) {
        if (alreadyFound) return true;
        return checkImageExists(candidate.url).then(function (exists) {
          if (!exists) return false;
          found = candidate;
          return true;
        });
      });
    });

    sequence.then(function (exists) {
      if (!exists || !found) return;
      var personalizedSrc = found.url;
      var personalizedSrcset = rewriteSrcset(originalSrcset, companyCode, found.base);

      img.setAttribute("data-b2b-original-src", originalSrc);
      img.setAttribute("data-b2b-personalized-src", personalizedSrc);

      if (originalSrcset) {
        img.setAttribute("data-b2b-original-srcset", originalSrcset);
        if (personalizedSrcset) {
          img.setAttribute("srcset", personalizedSrcset);
        }
      }

      img.setAttribute("src", personalizedSrc);
    });
  }

  function scan(container) {
    var scope = container || document;
    var images = scope.querySelectorAll(ALL_SELECTOR);
    // CRO round 5: one image that cannot be parsed must not stop the rest of the scan,
    // and must never leave the pending class in place. Guarded per image.
    images.forEach(function (img) {
      try {
        queueImage(img);
      } catch (err) {
        if (window.console && console.warn) {
          console.warn("b2b image personalization skipped an image", err);
        }
      }
    });
  }

  function observeDom() {
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (!(node instanceof Element)) return;

          if (node.matches && node.matches(ALL_SELECTOR)) {
            queueImage(node);
            return;
          }

          if (node.querySelectorAll) {
            scan(node);
          }
        });
      });
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  function init() {
    // CRO round 5: the b2b-images-pending class sets opacity 0 on every personalised
    // image until this function clears it. Before this change, anything that threw in
    // scan or observeDom meant clearPendingState never ran, so a B2B customer was left
    // looking at a page of invisible images that a refresh did not cure. The finally
    // block makes the page visible whatever happens above it.
    try {
      scan(document);
      observeDom();
    } catch (err) {
      if (window.console && console.warn) {
        console.warn("b2b image personalization stopped early", err);
      }
    } finally {
      clearPendingState();
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
